<template>
  <div class="university-survey-report">
    <div v-if="isLoading" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="spinner"></div>
    </div>
    <h3 class="text-2xl font-semibold text-gray-900 mb-4">
      University-level Module Questionnaire Report
    </h3>
    <div class="bg-white shadow-md rounded-lg p-6">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner"></div>
        <p class="mt-2 text-gray-600">Loading university survey data...</p>
      </div>
      <div v-else-if="error" class="text-red-500 text-center py-4">
        {{ error }}
      </div>
      <div v-else>
        <!-- Survey selection dropdown -->

        <div class="mb-4">
          <label for="survey-select" class="block text-sm font-medium text-gray-700 mb-2">
            Select a Survey:
          </label>
          <select id="survey-select" v-model="selectedSurveyId" @change="loadSurveyData"
            class="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
            <option value="">Please select a survey</option>
            <option v-for="survey in surveys" :key="survey.id" :value="survey.id">
              {{ survey.title }}
            </option>
          </select>
          <!-- button for release -->
          <button
            v-if="selectedSurveyId && surveys.find(x => x.id === selectedSurveyId).released === false && user.role == 'admin'"
            @click="releaseSurvey"
            class="mt-2 bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition duration-300 mt-2">
            Release Survey
          </button>

          <!-- button for revoke -->
          <button
            v-if="selectedSurveyId && surveys.find(x => x.id === selectedSurveyId).released === true && user.role == 'admin'"
            @click="revokeSurvey"
            class="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-600-900 transition duration-300 mt-2">
            Revoke Release
          </button>

          <button v-if="selectedSurveyId && user.role == 'admin'" @click="downloadCompletedStudentList"
            class="ml-2 mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-600-900 transition duration-300 mt-2">
            Completed Student List
          </button>
        </div>

        <!-- Report Type selection dropdown -->
        <div class="mb-4">
          <label for="report-type-select" class="block text-sm font-medium text-gray-700 mb-2">
            Select Report Type:
          </label>
          <select id="report-type-select"
            :disabled="!user || (user.role == 'school_admin' || user.role == 'department_admin')" v-model="reportType"
            @change="loadSurveyData"
            class="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
            <option value="module-teacher">Module-Teacher Report</option>
            <option value="university">University Report</option>
          </select>
        </div>

        <!-- Survey level selection dropdown -->
        <!-- <div class="mb-4" v-if="reportType == 'module-teacher'">
          <label for="level-select" class="block text-sm font-medium text-gray-700 mb-2">
            Select Report Level:
          </label>
          <select
            id="level-select"
            v-model="selectedLevel"
            @change="loadSurveyData"
            class="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="module" v-if="user.role=='admin' || user.role=='supervisor' || user.groups.length > 0">Module</option>
            <option value="department">Department</option>
            <option value="school">School</option>
            <option value="university">University</option>
          </select>
        </div>-->



        <!-- Survey data display area -->
        <div v-if="selectedSurveyId" class="mt-6 space-y-6">
          <!--  <ResponseTable :data="surveyData.responseStats" /> -->
          <div v-if="reportType === 'module-teacher'" id="report">
            <div
              class="bg-gray-600 mt-4 shadow-lg overflow-hidden bg-primary p-2 text-xl font-semibold flex justify-center text-white"
              style="text-decoration: underline;">Module-Teacher Report</div>
            <div class="bg-white mb-4 p-2 text-xl font-bold flex justify-center text-black"> {{selectedSurveyId ?
              surveys.find(x => x.id === selectedSurveyId).title : ""}}</div>
            <HierarchicalRatingsTable
              v-if="user.role == 'admin' || user.role == 'supervisor' || user.groups.length > 0 || user.role == 'Staff'"
              class="mt-2 mb-2" :surveyId="selectedSurveyId" :data="surveyDataModule" selected-level="module"
              :fullPage="fullPage" />
            <HierarchicalRatingsTable class="mt-2 mb-2" :surveyId="selectedSurveyId" :data="surveyDataDepartment"
              selected-level="department" :fullPage="fullPage" />
            <HierarchicalRatingsTable class="mt-2 mb-2" :surveyId="selectedSurveyId" :data="surveyDataSchool"
              selected-level="school" :fullPage="fullPage" />
            <!--  <HierarchicalRatingsTable
            class="mt-2 mb-2"
              :surveyId="selectedSurveyId"
              :data="surveyDataUniversity"
              selected-level="university"
            />-->
          </div>
          <div v-else id="report">
            <div
              class="bg-gray-600 mt-4 shadow-lg overflow-hidden bg-primary p-2 text-xl font-semibold flex justify-center text-white"
              style="text-decoration: underline;">University Report</div>
            <div class="bg-white mb-4 p-2 text-xl font-bold flex justify-center text-black"> {{selectedSurveyId ?
              surveys.find(x => x.id === selectedSurveyId).title : ""}}</div>
            <!-- Add University Report specific components here -->
            <SchoolDepartmentReport :data="surveyData" />
          </div>
        </div>
      </div>
    </div>

    
    <div>
      <button class="mt-2" style="text-decoration: underline;cursor: pointer;" @click="generatePDFContent('report')" v-if="selectedSurveyId">Download
        PDF</button>
    </div>
    <div>
      <button class="mt-2" style="text-decoration: underline;cursor: pointer;" v-if="reportType === 'module-teacher' && selectedSurveyId"
        @click="generateOptionalCommentsPDFContent">Download Optional Comments PDF</button>
    </div>

    <div class="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-2xl sm:w-full" style="visibility: hidden;">
          <div class="bg-primary text-white px-4 py-2 flex justify-between items-center">
            <h3 class="text-lg leading-6 font-large">Comments</h3>
            <button @click="isCommentShow = false" class="text-white font-large">&times;</button>
          </div>
          <div class="px-4 py-5 sm:p-6"  id="comments">
            <table class="min-w-full divide-y divide-gray-200 border mb-4">
              <thead class="bg-black text-white divide-y divide-gray-200">
                <th class="px-4 py-2 text-left">Module Code</th>

                <th class="px-4 py-2 text-left">Staff</th>
                <th class="px-4 py-2 text-left">Comments</th>

              </thead>
              <tbody class="bg-white divide-y divide-gray-200" v-if="comments.length > 0">
                <tr v-for="(comment, index) in comments" :key="index" class="hover:bg-gray-50">
                  <td class="px-4 py-2 text-left">{{ comment.moduleCode }}</td>
                  <td class="px-4 py-2 text-left">{{ getStaffName(comment.staffId) }}</td>
                  <td class="px-4 py-2 text-left">{{ comment.comments }}</td>
                </tr>
              </tbody>
              <tbody v-else>
                No Comments
              </tbody>
            </table>
          </div>
      </div>
  </div>
</template>

<script>
import { jsPDF } from "jspdf";
import { surveyApi } from "@/api/surveyManagement";
import ResponseTable from "./ResponseTable.vue";
import HierarchicalRatingsTable from "./HierarchicalRatingsTable.vue";
import SchoolDepartmentReport from "./SchoolDepartmentReport.vue";
import { mapState } from "vuex";
import { reportsApi } from "@/api/reports";

export default {
  name: "UniversitySurveyReport",
  components: {
    ResponseTable,
    HierarchicalRatingsTable,
    SchoolDepartmentReport
  },
  data() {
    return {
      loading: true,
      isLoading: false,
      fullPage: false,
      error: null,
      surveys: [],
      selectedSurveyId: "",
      surveyData: null,
      surveyDataModule: null,
      surveyDataDepartment: null,
      surveyDataSchool: null,
      surveyDataUniversity: null,
      selectedLevel: "university",
      reportType: "module-teacher",
      comments:[]
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      staff: (state) => state.staff,
    }),
  },
  mounted() {
    if (this.user.role == 'department_admin' || this.user.role == 'school_admin')
      this.reportType = 'university'

    this.fetchSurveys();
  },
  methods: {
    getStaffName(id){
      return this.staff.find(x=>x.staffId == id) ? this.staff.find(x=>x.staffId == id).name : id
    },
    async generateOptionalCommentsPDFContent() {
      console.log(this.surveyDataModule)  
      let _results = this.surveyDataModule.moduleResults
      let _modules = _results.map(x => x.moduleCode)
      //distinct _modules
      _modules = [...new Set(_modules)]
      //console.log(_modules)
      this.comments = await surveyApi.getSurveyResponseCommentsByModuleList(this.selectedSurveyId, _modules)

      if (this.comments.length > 0)
        this.generatePDFContent("comments")
    },
    generatePDFContent(type = "report") {
      const doc = new jsPDF('p','mm','a3');
      // Show loading animation
      this.isLoading = true;
      this.fullPage = true;
      // Get the content of the element you want to include in the PDF
      const content = document.getElementById(type);
      console.log(type)
      console.log(content)
      // Get the width of the page and the content
      const pageWidth = doc.internal.pageSize.width;

      // Convert content to PDF with scaling based on page width
      doc.html(content, {
        callback: (doc) => {
          // Save the generated PDF
          doc.save("ReportDownload.pdf");

          // Hide loading animation
          this.isLoading = false;
        },
        margin: [10, 10, 10, 10], // Optional: set margins
        x: 10, // X position
        y: 10, // Y position
        width: pageWidth - 50, // Ensure content width fits within the page
        windowWidth: content.scrollWidth, // Set window width to content width
      });

      setTimeout(() => {
        this.$nextTick(() => {
          this.fullPage = false;
        });
      }, 1000);
    },
    async downloadCompletedStudentList() {
      let blob = await reportsApi.exportCompletedStudentReport(this.selectedSurveyId)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `StudentList.xlsx`
      );
      link.setAttribute("download", `StudentList.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async releaseSurvey() {
      let survey = this.surveys.find(x => x.id === this.selectedSurveyId)
      survey.released = true
      await surveyApi.update(survey.id, survey)
      alert('Survey released successfully')
    },
    async revokeSurvey() {
      let survey = this.surveys.find(x => x.id === this.selectedSurveyId)
      survey.released = false
      await surveyApi.update(survey.id, survey)
      alert('Survey revoked successfully')
    },
    async fetchSurveys() {
      try {
        this.loading = true;
        let _surveys = await surveyApi.getAll();
        _surveys = _surveys.filter(x => x.type == 'University' && x.status == 'Published')
        if (this.user.role != 'admin')
          this.surveys = _surveys.filter(x => x.released)
        else
          this.surveys = _surveys
      } catch (error) {
        console.error("Error fetching surveys:", error);
        this.error = "Failed to load surveys. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async loadSurveyData() {
      if (!this.selectedSurveyId) return;

      try {
        this.loading = true;
        if (this.reportType === "module-teacher") {
          //this.surveyData = await surveyApi.getUniversitySurveyResults(this.selectedSurveyId, this.selectedLevel);
          this.surveyDataModule = await surveyApi.getUniversitySurveyResults(this.selectedSurveyId, "module");
          this.surveyDataDepartment = await surveyApi.getUniversitySurveyResults(this.selectedSurveyId, "department");
          this.surveyDataSchool = await surveyApi.getUniversitySurveyResults(this.selectedSurveyId, "school");
          //this.surveyDataUniversity = await surveyApi.getUniversitySurveyResults(this.selectedSurveyId, "university"); 
        } else {
          let _level = 'school-department'
          this.surveyData = await surveyApi.getUniversitySurveyResults(this.selectedSurveyId, _level);
        }
      } catch (error) {
        console.error("Error loading survey data:", error);
        this.error = "Failed to load survey data. Please try again.";
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>